import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import Nav from "components/layout/Nav";
import { updateTrek, featchTrek } from '../../../../Slices/trek.slice';
import { TrekEditDetailsMenubar } from 'components/layout/MenuBar';
import { Overview } from 'components/forms/overview';
import { MajorAttractions, TrekProducts, ImageGallery } from 'components/forms/commonForms';
import { ThumbnailPicker } from 'components/forms/thumbnailPicker';
import { AddLocalGuide, GradeWiseInsights, SuggestedItinerary } from 'components/forms/forms';
import { HowToReachTrek } from 'components/forms/howToReach';
import { showToast } from 'components/shared/Toast';
import { buildEditDetailsPath, matchesLocationParam } from 'utils/locationSlug';

const TrekEditDetails = () => {
    const { slug, form } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { trek, status, error } = useSelector((state) => state.trek);
    const { user } = useSelector((state) => state.auth);
    const [selectedLocation, setSelectedLocation] = useState(null);
    const [selectedForm, setSelectedForm] = useState(form || "Overview");
    const [isApproving, setIsApproving] = useState(false);

    useEffect(() => {
        dispatch(featchTrek());
    }, [dispatch]);

    useEffect(() => {
        if (slug && trek.length) {
            const location = trek.find((loc) => matchesLocationParam(loc, slug));
            setSelectedLocation(location || null);
        }
    }, [slug, trek]);

    useEffect(() => {
        if (form) {
            setSelectedForm(form);
        }
    }, [form]);

    const handleFormSelection = (formName) => {
        setSelectedForm(formName);
        if (selectedLocation) {
            navigate(buildEditDetailsPath("trek", selectedLocation, formName), { replace: true });
        }
    };

    const formComponents = {
        Overview: (props) => <Overview {...props} />,
        MajorAttractions: (props) => <MajorAttractions {...props} />,
        GradeWiseInsights: (props) => <GradeWiseInsights {...props} />,
        TrekProducts: (props) => <TrekProducts {...props} />,
        ImageGallery: (props) => <ImageGallery {...props} />,
        ThumbnailImages: (props) => <ThumbnailPicker {...props} />,
        LocalGuide: (props) => <AddLocalGuide {...props} />,
        HowtoReach: (props) => <HowToReachTrek {...props} />,
        SuggestedItinerary: (props) => <SuggestedItinerary {...props} />
    };

    const handleFormSubmit = async (formData) => {
        if (selectedLocation) {
            try {
                const response = await dispatch(updateTrek({ id: selectedLocation._id, formData })).unwrap();
                if (response) {
                    const nextLocation = { ...selectedLocation, ...response };
                    dispatch(featchTrek());
                    setSelectedLocation(nextLocation);
                    navigate(buildEditDetailsPath("trek", nextLocation, selectedForm), { replace: true });
                }
            } catch (error) {
                console.error("❌ Error updating form:", error);
            }
        }
    };

    const handleApproveChanges = async () => {
        if (!selectedLocation || isApproving) return;
        try {
            setIsApproving(true);
            await dispatch(
                updateTrek({
                    id: selectedLocation._id,
                    formData: { reviewNote: "Approved by District Manager" },
                })
            ).unwrap();
            dispatch(featchTrek());
            showToast("Changes approved and sent to Super Admin.", "success");
        } catch (error) {
            console.error("❌ Error approving changes:", error);
            showToast("Failed to approve changes.", "error");
        } finally {
            setIsApproving(false);
        }
    };


    const renderForm = () => {
        const SelectedFormComponent = formComponents[selectedForm];
        return SelectedFormComponent ? (
            <SelectedFormComponent
                location={selectedLocation}
                onSubmit={handleFormSubmit}
                parentRange={true}
                contentType="trek"
            />
        ) : <p>Form not available</p>;
    };

    return (
        <>
            <Nav />
            <div className="row">
                <TrekEditDetailsMenubar
                    selectedForm={selectedForm}
                    setActiveComponent={handleFormSelection}
                    currentLocation={selectedLocation}
                />
                <main className="adminPageMain adminPageMain--slate">
                    {status === 'loading' && (
                        <div className="adminPageShell">
                            <p>Loading location details...</p>
                        </div>
                    )}
                    {error && (
                        <div className="adminPageShell">
                            <p style={{ color: 'red' }}>Error: {error}</p>
                        </div>
                    )}
                    {!selectedLocation && status !== "loading" && (
                        <div className="adminPageShell">
                            <p>No location selected for editing.</p>
                        </div>
                    )}
                    {selectedLocation && (
                        <div className="adminPageShell">
                            <section className="editorPageShell">
                                {user?.role === "districtManager" && selectedLocation.approvalStatus === "pending_dm" && (
                                    <div className="editorPageActions">
                                        <button
                                            className="btn-blue"
                                            onClick={handleApproveChanges}
                                            disabled={isApproving}
                                            type="button"
                                        >
                                            {isApproving ? "Approving..." : "Approve Changes"}
                                        </button>
                                    </div>
                                )}
                                <div className="formWrapper">
                                    {renderForm()}
                                </div>
                            </section>
                        </div>
                    )}
                </main>
            </div>
        </>
    );
};

export default TrekEditDetails;
